import React, { useState } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import Header from './components/Header';
import Hero from './components/Hero';
import VideoUploadForm from './components/VideoUploadForm';
import Features from './components/Features';
import ReviewResults from './components/ReviewResults';
import StandardAnalysisResults from './components/StandardAnalysisResults';
import PlatformInfo from './components/PlatformInfo';
import Footer from './components/Footer';
import AnalysisError from './components/AnalysisError';
import FrameworksPage from './components/FrameworksPage';
import WhyArcRefineryPage from './components/WhyArcRefineryPage';
import PlatformsPage from './components/PlatformsPage';
import YouTubeVideo from './components/YouTubeVideo';

function AppContent() {
  const [reviewData, setReviewData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);

  const handleReviewComplete = (data) => {
    setReviewData(data);
    setAnalysisError(null);
  };

  const handleNewReview = () => {
    setReviewData(null);
    setAnalysisError(null);
  };

  const handleAnalysisError = (error) => {
    setAnalysisError(error);
    setReviewData(null);
  };

  const handleRetryAnalysis = () => {
    setAnalysisError(null);
    // This will be handled by the form component
  };

  const handleSwitchToBasic = () => {
    setAnalysisError(null);
    // The form component will handle switching to basic mode
  };


  return (
    <Router>
    <div className="min-h-screen">
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
        }}
      />
      
        <Header />

        <Routes>
          {/* Main Home Page Route */}
          <Route path="/" element={
            <>
              {/* Top section with vibrant arc-style background */}
              <div className="relative bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 overflow-hidden">
                {/* Vibrant Arc Background Pattern */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  {/* Large purple arc - top left */}
                  <div className="absolute -top-20 -left-20 w-96 h-96 opacity-40">
                    <div className="absolute inset-0 rounded-full border-[40px] border-purple-300"></div>
                    <div className="absolute inset-8 rounded-full border-[30px] border-purple-400"></div>
                    <div className="absolute inset-16 rounded-full border-[20px] border-purple-500"></div>
                  </div>
                  
                  {/* Pink arc - top right */}
                  <div className="absolute -top-32 right-20 w-80 h-80 opacity-30">
                    <div className="absolute inset-0 rounded-full border-[35px] border-pink-300"></div>
                    <div className="absolute inset-10 rounded-full border-[25px] border-pink-400"></div>
                  </div>
                  
                  {/* Blue arc - bottom left */}
                  <div className="absolute -bottom-24 -left-16 w-72 h-72 opacity-35">
                    <div className="absolute inset-0 rounded-full border-[30px] border-blue-300"></div>
                    <div className="absolute inset-8 rounded-full border-[20px] border-blue-400"></div>
                  </div>
                  
                  {/* Small accent arcs */}
                  <div className="absolute top-1/3 right-1/4 w-48 h-48 opacity-25">
                    <div className="absolute inset-0 rounded-full border-[20px] border-indigo-300"></div>
                  </div>
                  
                  <div className="absolute bottom-1/4 right-1/3 w-40 h-40 opacity-30">
                    <div className="absolute inset-0 rounded-full border-[15px] border-orange-300"></div>
                  </div>
                </div>

        <main className="relative container mx-auto px-4 py-8">
          {analysisError ? (
            <AnalysisError
              error={analysisError.error}
              details={analysisError.details}
              onRetry={handleRetryAnalysis}
              onSwitchToBasic={handleSwitchToBasic}
            />
          ) : !reviewData ? (
            <>
              <Hero />
              
              <VideoUploadForm
                onAnalysisComplete={handleReviewComplete}
                onAnalysisError={handleAnalysisError}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
              />
            </>
          ) : (
            reviewData.reportType === 'standard' ? (
              <StandardAnalysisResults
                data={reviewData}
                onNewReview={handleNewReview}
              />
            ) : (
              <ReviewResults
                data={reviewData}
                onNewReview={handleNewReview}
              />
            )
          )}
        </main>
      </div>

      {/* Bottom section with white background */}
      {!analysisError && !reviewData && (
        <div className="bg-white">
          <main className="container mx-auto px-4 py-8">
            {/* YouTube Video Section */}
            <YouTubeVideo videoId="RmyyKCR4PW0" title="ArcRefinery Demo Video" />
            
            {/* Frameworks Showcase */}
                  <section id="frameworks" className="max-w-7xl mx-auto mb-16 md:mb-20 px-4 md:px-6">
                    {/* For Brands, Agencies, Creators Section */}
                    <div className="mb-16 md:mb-20">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
                        {/* Brand Teams */}
                        <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl shadow-lg overflow-hidden">
                          <div className="p-5 md:p-6">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                </svg>
                              </div>
                              <div className="flex-1">
                                <h3 className="font-bold text-white text-base md:text-lg">Brand Teams</h3>
                                <p className="text-blue-100 text-sm">Specialized solutions for your workflow</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white border-t border-blue-200 p-6 md:p-7 rounded-b-xl">
                            <div className="space-y-4 text-sm leading-relaxed">
                              <div className="flex items-start">
                                <svg className="w-3 h-3 text-blue-500 mr-1.5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <p className="text-gray-700"><span className="font-semibold">Protect Brand Reputation</span> - Validate before it represents your company</p>
                              </div>
                              <div className="flex items-start">
                                <svg className="w-3 h-3 text-blue-500 mr-1.5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <p className="text-gray-700"><span className="font-semibold">Maximize Marketing Budget</span> - Only back qualified videos</p>
                              </div>
                              <div className="flex items-start">
                                <svg className="w-3 h-3 text-blue-500 mr-1.5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <p className="text-gray-700"><span className="font-semibold">Faster Decision Making</span> - Instant validation & confidence</p>
                              </div>
                              <div className="flex items-start">
                                <svg className="w-3 h-3 text-blue-500 mr-1.5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <p className="text-gray-700"><span className="font-semibold">Stakeholder Confidence</span> - Marketing Science backed recommendations</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Agency Teams */}
                        <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl shadow-lg overflow-hidden">
                          <div className="p-5 md:p-6">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                              </div>
                              <div className="flex-1">
                                <h3 className="font-bold text-white text-base md:text-lg">Agency Teams</h3>
                                <p className="text-purple-100 text-sm">Specialized solutions for your workflow</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white border-t border-purple-200 p-6 md:p-7 rounded-b-xl">
                            <div className="space-y-4 text-sm leading-relaxed">
                              <div className="flex items-start">
                                <svg className="w-3 h-3 text-purple-500 mr-1.5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <p className="text-gray-700"><span className="font-semibold">Win More Pitches</span> - Validate creative before spending client money</p>
                              </div>
                              <div className="flex items-start">
                                <svg className="w-3 h-3 text-purple-500 mr-1.5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <p className="text-gray-700"><span className="font-semibold">Reduce Client Testing Costs</span> - Pre-screen before expensive tests</p>
                              </div>
                              <div className="flex items-start">
                                <svg className="w-3 h-3 text-purple-500 mr-1.5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <p className="text-gray-700"><span className="font-semibold">Justify Premium Pricing</span> - Clients pay more for validated excellence</p>
                              </div>
                              <div className="flex items-start">
                                <svg className="w-3 h-3 text-purple-500 mr-1.5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <p className="text-gray-700"><span className="font-semibold">Scale Campaign Success</span> - Build a library of what works and why</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Content Creators */}
                        <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl shadow-lg overflow-hidden">
                          <div className="p-5 md:p-6">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              </div>
                              <div className="flex-1">
                                <h3 className="font-bold text-white text-base md:text-lg">Content Creators</h3>
                                <p className="text-orange-100 text-sm">Specialized solutions for your workflow</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white border-t border-orange-200 p-6 md:p-7 rounded-b-xl">
                            <div className="space-y-4 text-sm leading-relaxed">
                              <div className="flex items-start">
                                <svg className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <p className="text-gray-700"><span className="font-semibold">Get Clear Direction on Impactful Videos</span> - Guidance on frameworks & elements</p>
                              </div>
                              <div className="flex items-start">
                                <svg className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <p className="text-gray-700"><span className="font-semibold">Create Videos Brands Want</span> - Master frameworks brands look for</p>
                              </div>
                              <div className="flex items-start">
                                <svg className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <p className="text-gray-700"><span className="font-semibold">Attract More Brand Partnerships</span> - Better videos = more brands wanting you</p>
                              </div>
                              <div className="flex items-start">
                                <svg className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <p className="text-gray-700"><span className="font-semibold">Secure Long-Term Collaborations</span> - Turn one-time deals into ongoing partnerships</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="text-center mb-8 md:mb-12">
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-4">
                        Built on Proven Marketing Frameworks
                      </h2>
                      <p className="text-gray-600 max-w-3xl mx-auto text-sm md:text-base lg:text-lg">
                        Our AI analysis leverages industry-leading frameworks used by top brands and agencies to deliver comprehensive insights and actionable recommendations.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12">
                      {/* Storytelling & Brand Building Frameworks */}
                      <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl md:rounded-2xl transform rotate-1"></div>
                        <div className="relative bg-white rounded-xl md:rounded-2xl p-5 md:p-6 lg:p-8 shadow-xl border border-indigo-100 min-h-[340px] md:h-96 flex flex-col justify-between">
                          <div className="flex items-start md:items-center mb-4 md:mb-6">
                            <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg md:rounded-xl flex items-center justify-center mr-3 md:mr-4 flex-shrink-0">
                              <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-base md:text-lg lg:text-xl font-bold text-gray-900">Storytelling & Brand Building</h3>
                              <p className="text-gray-600 text-xs md:text-sm">Narrative frameworks that build emotional connections</p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2 md:gap-3 lg:gap-4">
                            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg md:rounded-xl p-3 md:p-4 border border-indigo-100">
                              <div className="flex items-center mb-1 md:mb-2">
                                <div className="w-5 h-5 md:w-6 md:h-6 bg-indigo-500 rounded-full flex items-center justify-center mr-1.5 md:mr-2 flex-shrink-0">
                                  <span className="text-white text-xs font-bold">A</span>
                                </div>
                                <h4 className="font-semibold text-gray-900 text-xs md:text-sm">Story Arc</h4>
                              </div>
                              <p className="text-[10px] md:text-xs text-gray-600 leading-tight">Hook → Problem → Tension → Climax → Resolution</p>
                            </div>
                            
                            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg md:rounded-xl p-3 md:p-4 border border-purple-100">
                              <div className="flex items-center mb-1 md:mb-2">
                                <div className="w-5 h-5 md:w-6 md:h-6 bg-purple-500 rounded-full flex items-center justify-center mr-1.5 md:mr-2 flex-shrink-0">
                                  <span className="text-white text-xs font-bold">3</span>
                                </div>
                                <h4 className="font-semibold text-gray-900 text-xs md:text-sm">3-Act Structure</h4>
                              </div>
                              <p className="text-[10px] md:text-xs text-gray-600 leading-tight">Setup → Confrontation → Resolution</p>
                            </div>
                            
                            <div className="bg-gradient-to-br from-pink-50 to-indigo-50 rounded-lg md:rounded-xl p-3 md:p-4 border border-pink-100">
                              <div className="flex items-center mb-1 md:mb-2">
                                <div className="w-5 h-5 md:w-6 md:h-6 bg-pink-500 rounded-full flex items-center justify-center mr-1.5 md:mr-2 flex-shrink-0">
                                  <span className="text-white text-xs font-bold">H</span>
                                </div>
                                <h4 className="font-semibold text-gray-900 text-xs md:text-sm">Hero's Journey</h4>
                              </div>
                              <p className="text-[10px] md:text-xs text-gray-600 leading-tight">Hero → Call → Mentor → Transformation → Return</p>
                            </div>
                            
                            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg md:rounded-xl p-3 md:p-4 border border-indigo-100">
                              <div className="flex items-center mb-1 md:mb-2">
                                <div className="w-5 h-5 md:w-6 md:h-6 bg-indigo-600 rounded-full flex items-center justify-center mr-1.5 md:mr-2 flex-shrink-0">
                                  <span className="text-white text-xs font-bold">R</span>
                                </div>
                                <h4 className="font-semibold text-gray-900 text-xs md:text-sm">HRR</h4>
                              </div>
                              <p className="text-[10px] md:text-xs text-gray-600 leading-tight">Hook → Relate → Resolution</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Conversion & ROI Frameworks */}
                      <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl md:rounded-2xl transform -rotate-1"></div>
                        <div className="relative bg-white rounded-xl md:rounded-2xl p-5 md:p-6 lg:p-8 shadow-xl border border-orange-100 min-h-[340px] md:h-96 flex flex-col justify-between">
                          <div className="flex items-start md:items-center mb-4 md:mb-6">
                            <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg md:rounded-xl flex items-center justify-center mr-3 md:mr-4 flex-shrink-0">
                              <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-base md:text-lg lg:text-xl font-bold text-gray-900">Conversion & ROI Tactics</h3>
                              <p className="text-gray-600 text-xs md:text-sm">Persuasion formulas that drive measurable results</p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2 md:gap-3 lg:gap-4">
                            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-lg md:rounded-xl p-3 md:p-4 border border-orange-100">
                              <div className="flex items-center mb-1 md:mb-2">
                                <div className="w-5 h-5 md:w-6 md:h-6 bg-orange-500 rounded-full flex items-center justify-center mr-1.5 md:mr-2 flex-shrink-0">
                                  <span className="text-white text-xs font-bold">A</span>
                                </div>
                                <h4 className="font-semibold text-gray-900 text-xs md:text-sm">AIDA</h4>
                              </div>
                              <p className="text-[10px] md:text-xs text-gray-600 leading-tight">Attention → Interest → Desire → Action</p>
                            </div>
                            
                            <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-lg md:rounded-xl p-3 md:p-4 border border-red-100">
                              <div className="flex items-center mb-1 md:mb-2">
                                <div className="w-5 h-5 md:w-6 md:h-6 bg-red-500 rounded-full flex items-center justify-center mr-1.5 md:mr-2 flex-shrink-0">
                                  <span className="text-white text-xs font-bold">P</span>
                                </div>
                                <h4 className="font-semibold text-gray-900 text-xs md:text-sm">PAS</h4>
                              </div>
                              <p className="text-[10px] md:text-xs text-gray-600 leading-tight">Problem → Agitate → Solution</p>
                            </div>
                            
                            <div className="bg-gradient-to-br from-pink-50 to-orange-50 rounded-lg md:rounded-xl p-3 md:p-4 border border-pink-100">
                              <div className="flex items-center mb-1 md:mb-2">
                                <div className="w-5 h-5 md:w-6 md:h-6 bg-pink-500 rounded-full flex items-center justify-center mr-1.5 md:mr-2 flex-shrink-0">
                                  <span className="text-white text-xs font-bold">B</span>
                                </div>
                                <h4 className="font-semibold text-gray-900 text-xs md:text-sm">BAB</h4>
                              </div>
                              <p className="text-[10px] md:text-xs text-gray-600 leading-tight">Before → After → Bridge</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Bottom CTA */}
                    <div className="text-center mt-8 md:mt-12">
                      <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 rounded-xl md:rounded-2xl p-5 md:p-6 lg:p-8 border border-indigo-100">
                        <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3">
                          Get AI-Powered Analysis Across All Frameworks
                        </h3>
                        <p className="text-gray-600 mb-4 md:mb-6 max-w-2xl mx-auto text-sm md:text-base">
                          Our comprehensive analysis evaluates your video against all these proven frameworks, giving you actionable insights to maximize both brand storytelling and conversion performance.
                        </p>
                        <div className="flex flex-wrap justify-center gap-2 md:gap-4 text-xs md:text-sm">
                          <div className="flex items-center bg-white rounded-full px-3 md:px-4 py-1.5 md:py-2 shadow-sm">
                            <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-1.5 md:mr-2"></div>
                            <span className="text-gray-700">Real-time scoring</span>
                          </div>
                          <div className="flex items-center bg-white rounded-full px-3 md:px-4 py-1.5 md:py-2 shadow-sm">
                            <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-blue-500 rounded-full mr-1.5 md:mr-2"></div>
                            <span className="text-gray-700">Actionable insights</span>
                          </div>
                          <div className="flex items-center bg-white rounded-full px-3 md:px-4 py-1.5 md:py-2 shadow-sm">
                            <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-purple-500 rounded-full mr-1.5 md:mr-2"></div>
                            <span className="text-gray-700">Platform optimization</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                  
                  <section id="why-arcrefinery">
                    <Features />
                  </section>
                  
                  <section id="platforms">
                    <PlatformInfo />
                  </section>
                </main>
              </div>
            )}
            <Footer />
            </>
          } />
          
          {/* Frameworks Page Route */}
          <Route path="/frameworks" element={<FrameworksPage />} />
          
          {/* Why ArcRefinery Page Route */}
          <Route path="/why-arcrefinery" element={<WhyArcRefineryPage />} />
          
          {/* Platforms Page Route */}
          <Route path="/platforms" element={<PlatformsPage />} />
        </Routes>
    </div>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
